import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';
import { useRealtimeEvents } from '@parsina/framework/clinical/src/hooks/useRealtimeEvents';
import { withdrawAppointmentRequest } from './useDoctorBooking';

// The doctor-room queue is set at the clinic desk, on another screen. The realtime
// socket pushes those changes instantly; polling is only the fallback for when the
// socket is down — slow while it's healthy, brisk when it isn't.
const POLL_FALLBACK_MS = 20000;
const POLL_IDLE_MS = 60000;

// An Iran-local ISO day → the UTC epoch-ms window [start, end) it covers.
// The scheduling query's end bound is inclusive (date_time <= end), so callers pass
// end - 1 to keep the next day's midnight out.
export function iranDayWindow(iso) {
  const start = Date.parse(`${iso}T00:00:00Z`) - IRAN_OFFSET_MS;
  return { start, end: start + 86_400_000 };
}

// One shape for every appointment fetch on this screen. durationMinutes / arrivedAt /
// visitStartedAt are what turn the list into a clock: waiting-since on arrived rows,
// visit-open-for on the in_visit hero.
const APPOINTMENT_FIELDS = `
  id
  patientId
  doctorId
  name
  time
  date
  dateTime
  schedulingMode
  status
  type
  specialty
  urgency
  queuePosition
  durationMinutes
  arrivedAt
  visitStartedAt
  clinicId
  source
  placeholderLabel
  reminderNote
  patient {
    phone
    phones { id number raw relation isPrimary }
  }
`;

// The selected day only — the doctor's whole history has no business on a day sheet.
const DAY_APPOINTMENTS_QUERY = `
  query DayAppointments($startTime: String, $endTime: String) {
    appointments(startTime: $startTime, endTime: $endTime) {
      ${APPOINTMENT_FIELDS}
    }
  }
`;

// The forgotten-visit sweep: every in_visit appointment, ANY day. A visit left open
// yesterday is invisible to a day-bounded query, which is exactly how it gets
// forgotten — so this one is deliberately unbounded (in_visit rows are at most a
// handful; completing them is the only way out of this result set).
const OPEN_VISITS_QUERY = `
  query OpenVisits {
    appointments(status: "in_visit") {
      ${APPOINTMENT_FIELDS}
    }
  }
`;

const MARK_ARRIVED = `
  mutation MarkArrived($appointmentId: ID!) {
    markArrived(appointmentId: $appointmentId) { id status }
  }
`;

const END_VISIT = `
  mutation EndVisit($id: ID!) {
    endVisit(id: $id) { id status }
  }
`;

// The clinic desk's non-money row ops (PM-0518, lib/receptionRowOps.js).
const CANCEL_APPOINTMENT = `
  mutation CancelAppointment($appointmentId: ID!) {
    cancelAppointment(appointmentId: $appointmentId) { id status }
  }
`;

const MARK_NO_SHOW = `
  mutation MarkNoShow($appointmentId: ID!) {
    markNoShow(appointmentId: $appointmentId) { id status }
  }
`;

const SET_NEXT_IN_QUEUE = `
  mutation SetNextInQueue($appointmentId: ID!) {
    setNextInQueue(appointmentId: $appointmentId) { id queuePosition }
  }
`;

const SET_APPOINTMENT_NOTE = `
  mutation SetAppointmentNote($appointmentId: ID!, $note: String!) {
    setAppointmentNote(appointmentId: $appointmentId, note: $note) { id reminderNote }
  }
`;

// ── The doctor-room queue (صف اتاق پزشک) ───────────────────────────────────────
// Entry order, mirroring get_queue_status in the scheduling service: in_visit pinned
// first, then the manual position the clinic desk set (unpositioned last), then
// urgency, then appointment time. The server is authoritative — if this drifts from
// it, the doctor and the desk disagree about who walks in next.
const URGENCY_RANK = { emergency: 0, urgent: 1 };

export function queueSort(a, b) {
  const statusRank = x => (x.status === 'in_visit' ? 0 : 1);
  const posRank = x => (x.queuePosition == null ? Number.MAX_SAFE_INTEGER : x.queuePosition);
  return (
    statusRank(a) - statusRank(b) ||
    posRank(a) - posRank(b) ||
    (URGENCY_RANK[a.urgency] ?? 2) - (URGENCY_RANK[b.urgency] ?? 2) ||
    String(a.time || '').localeCompare(String(b.time || ''))
  );
}

// The patients physically here for this doctor, in the order they go in.
export function queueFor(appointments) {
  return appointments
    .filter(a => a.status === 'arrived' || a.status === 'in_visit')
    .sort(queueSort);
}

// ── Open-visit state machine thresholds ────────────────────────────────────────
// elapsed ≤ duration        → normal
// duration < elapsed ≤ 2×   → 'over' (amber)
// 2× < elapsed              → 'critical' (red)
// A visit open past FORGOTTEN_MS, or started before today's Iran day, is presumed
// forgotten and surfaces as a banner on every date the doctor navigates to.
export const FORGOTTEN_MS = 2 * 60 * 60 * 1000;

export function visitOverrun(apt, nowMs) {
  const started = Number(apt?.visitStartedAt);
  if (!started) return { elapsedMin: null, level: 'normal' };
  const elapsedMin = Math.max(0, Math.floor((nowMs - started) / 60000));
  const dur = apt.durationMinutes || 30;
  const level = elapsedMin > 2 * dur ? 'critical' : elapsedMin > dur ? 'over' : 'normal';
  return { elapsedMin, level };
}

// Minutes the patient has been sitting in the waiting room. null when the desk never
// marked them arrived — there is no wait to measure, only a booked slot time.
export function waitedMinutes(apt, nowMs) {
  const arrived = Number(apt?.arrivedAt);
  if (!arrived) return null;
  return Math.max(0, Math.floor((nowMs - arrived) / 60000));
}

// A visit nobody is going to come back and close on its own: open longer than
// FORGOTTEN_MS, or started before the current Iran-local day began.
export function forgottenVisits(openVisits, nowMs) {
  const iranNow = new Date(nowMs + IRAN_OFFSET_MS);
  const todayStartMs =
    Date.UTC(iranNow.getUTCFullYear(), iranNow.getUTCMonth(), iranNow.getUTCDate()) - IRAN_OFFSET_MS;
  return openVisits.filter(a => {
    const started = Number(a.visitStartedAt);
    if (!started) return false; // no clock — pre-migration row; the hero still shows it today
    return nowMs - started > FORGOTTEN_MS || started < todayStartMs;
  });
}

// ── Data hook ──────────────────────────────────────────────────────────────────
// selISO: the Iran-local day پذیرش is showing. Day list and open-visit sweep
// poll together; each keeps its last good result through a transient failure.
// `error` reports a failed initial load of the DAY LIST — even when the open-visit
// sweep succeeded, because an empty day sheet over a failed query reads as «no
// appointments today», which is a claim about the clinic, not about the network.
export function useReception(selISO) {
  const [appointments, setAppointments] = useState([]);
  const [openVisits, setOpenVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const selRef = useRef(selISO);
  selRef.current = selISO;

  const fetchDay = useCallback(async (iso) => {
    const { start, end } = iranDayWindow(iso);
    const data = await bffQuery(DAY_APPOINTMENTS_QUERY, {
      startTime: String(start),
      endTime: String(end - 1),
    });
    // A slow response for a day the user has already navigated away from must not
    // overwrite the day they are looking at.
    if (selRef.current === iso) setAppointments(data?.appointments || []);
  }, []);

  const fetchOpenVisits = useCallback(async () => {
    const data = await bffQuery(OPEN_VISITS_QUERY);
    setOpenVisits(data?.appointments || []);
  }, []);

  const reload = useCallback(async ({ initial = false } = {}) => {
    const iso = selRef.current;
    if (initial) setLoading(true);
    const results = await Promise.allSettled([fetchDay(iso), fetchOpenVisits()]);
    if (initial) {
      // The day list decides: without it there is nothing honest to show. A failed
      // open-visit sweep alone only hides the forgotten-visit alert.
      const [dayResult] = results;
      setError(dayResult.status === 'rejected' ? dayResult.reason : null);
      setLoading(false);
    }
    results.forEach(r => {
      if (r.status === 'rejected') console.error('reception fetch error:', r.reason);
    });
  }, [fetchDay, fetchOpenVisits]);

  // Initial load + reload on day change.
  useEffect(() => {
    reload({ initial: true });
  }, [selISO, reload]);

  // Realtime: any scheduling event for this doctor (arrival, in-visit, reorder,
  // complete/cancel) triggers a refetch. Debounced — a reorder can land as a burst
  // of events, and one refetch settles them all.
  const debounceRef = useRef(null);
  const onRealtime = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => reload(), 300);
  }, [reload]);
  const { connected: realtimeConnected } = useRealtimeEvents(onRealtime);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Poll as a safety net: slow while the socket is live (it carries the real
  // updates), brisk when it isn't (polling is all we have).
  useEffect(() => {
    const interval = realtimeConnected ? POLL_IDLE_MS : POLL_FALLBACK_MS;
    const id = setInterval(() => reload(), interval);
    return () => clearInterval(id);
  }, [reload, realtimeConnected]);

  const retry = useCallback(() => reload({ initial: true }), [reload]);

  // Clinical writes are never optimistic (DC §8): the row changes when the
  // server says so. Both reject on failure so the caller can SAY it failed —
  // they used to end in console.error, and the row just quietly reverted.
  const markArrived = useCallback(async (appointmentId) => {
    try {
      await bffMutation(MARK_ARRIVED, { appointmentId });
    } finally {
      reload();
    }
  }, [reload]);

  // پایان ویزیت from پذیرش — the escape hatch for an overrunning or
  // forgotten visit, same completion path the Visit screen uses.
  const endVisit = useCallback(async (appointmentId) => {
    try {
      await bffMutation(END_VISIT, { id: appointmentId });
      setAppointments(prev => prev.map(a => (a.id === appointmentId ? { ...a, status: 'completed' } : a)));
      setOpenVisits(prev => prev.filter(a => a.id !== appointmentId));
    } finally {
      reload();
    }
  }, [reload]);

  // «لغو درخواست» — the doctor's OWN booking still awaiting the clinic. Rejects on
  // failure so the row can say so; the row leaves the list when the server says.
  const withdrawRequest = useCallback(async (appointmentId) => {
    try {
      await withdrawAppointmentRequest(appointmentId);
    } finally {
      reload();
    }
  }, [reload]);

  // The row ops (PM-0518). Same contract as the writes above: never optimistic,
  // reject on failure so the caller can say so, the row changes on the reload.
  const writeThenReload = useCallback(async (doc, vars) => {
    try {
      return await bffMutation(doc, vars);
    } finally {
      reload();
    }
  }, [reload]);
  const cancelAppointment = useCallback(
    (appointmentId) => writeThenReload(CANCEL_APPOINTMENT, { appointmentId }), [writeThenReload]);
  const markNoShow = useCallback(
    (appointmentId) => writeThenReload(MARK_NO_SHOW, { appointmentId }), [writeThenReload]);
  const setNextInQueue = useCallback(
    (appointmentId) => writeThenReload(SET_NEXT_IN_QUEUE, { appointmentId }), [writeThenReload]);
  const setAppointmentNote = useCallback(
    (appointmentId, note) => writeThenReload(SET_APPOINTMENT_NOTE, { appointmentId, note }), [writeThenReload]);

  return {
    appointments, openVisits, loading, error, retry, markArrived, endVisit, withdrawRequest, reload,
    cancelAppointment, markNoShow, setNextInQueue, setAppointmentNote,
  };
}

// ── Week ahead ─────────────────────────────────────────────────────────────────
// Appointment counts for the next 7 Iran-local days — one bounded query, refreshed
// when the day rolls over. Cancelled rows don't count toward a day's workload.
const WEEK_COUNT_QUERY = `
  query WeekAppointments($startTime: String, $endTime: String) {
    appointments(startTime: $startTime, endTime: $endTime) {
      id
      date
      status
    }
  }
`;

// Counts for the seven Iran-local days starting at `startISO`. The desktop rail
// asks for the week AHEAD (starting today); the mobile week sheet asks for a
// Jalali week (starting Saturday) and pages through them — one counting rule
// for both, so a day showing three appointments in one shows three in the other.
//
// Answers are cached per start date for the session and re-asked when older
// than 60s, so paging back and forth in the sheet does not re-fetch every tap.
const WEEK_COUNT_TTL_MS = 60_000;
const weekCountCache = new Map(); // startISO → { at, counts: Map(iso → n) }

export function useWeekCounts(startISO, { enabled = true } = {}) {
  const [byDay, setByDay] = useState(() => weekCountCache.get(startISO)?.counts || null);

  useEffect(() => {
    if (!startISO || !enabled) return undefined;
    const cached = weekCountCache.get(startISO);
    setByDay(cached ? cached.counts : null);
    if (cached && Date.now() - cached.at < WEEK_COUNT_TTL_MS) return undefined;
    let alive = true;
    const { start } = iranDayWindow(startISO);
    bffQuery(WEEK_COUNT_QUERY, {
      startTime: String(start),
      endTime: String(start + 7 * 86_400_000 - 1),
    })
      .then(data => {
        const counts = new Map();
        (data?.appointments || []).forEach(a => {
          if (a.status === 'cancelled' || !a.date) return;
          counts.set(a.date, (counts.get(a.date) || 0) + 1);
        });
        weekCountCache.set(startISO, { at: Date.now(), counts });
        if (alive) setByDay(counts);
      })
      .catch(err => console.error('weekCounts error:', err));
    return () => { alive = false; };
  }, [startISO, enabled]);

  return useMemo(() => {
    if (!startISO) return [];
    const base = Date.parse(`${startISO}T12:00:00Z`);
    return Array.from({ length: 7 }, (_, i) => {
      const iso = new Date(base + i * 86_400_000).toISOString().split('T')[0];
      return { iso, count: byDay ? (byDay.get(iso) || 0) : null };
    });
  }, [startISO, byDay]);
}

// The week ahead, for the desktop rail.
export function useWeekAhead(todayISO) {
  return useWeekCounts(todayISO);
}

// ── Today's income ─────────────────────────────────────────────────────────────
// The doctor's share of today's invoiced work, in MINOR UNITS of its currency as it
// comes off the wire (whole rials for IRR) — the display conversion belongs to the
// display edge. Aggregates cover the whole window regardless of pageSize, so one
// line is all we need to carry them.
//
// The day spans every clinic the doctor works at, and those can keep their books
// in different currencies. The scalar totals are then NULL — there is no single
// currency to state them in — so the figures are read per currency from
// `totalsByCurrency`, and a `|| 0` can never turn «two currencies» into «nothing».
const TODAY_INCOME_QUERY = `
  query TodayIncome($fromTs: Float, $toTs: Float) {
    doctorAccountingReport(fromTs: $fromTs, toTs: $toTs, pageSize: 1) {
      doctorShareTotal
      collectedDoctorShare
      totalsByCurrency { currency doctorShareTotal collectedDoctorShare }
      mixedCurrency
      types {
        itemType
        count
      }
    }
  }
`;

const unsettledOf = (share, collected) => Math.max(0, (share || 0) - (collected || 0));

/**
 * The report → what the income cards read:
 *
 *   { shareRial, unsettledRial, visitCount, byCurrency }
 *
 * `byCurrency` is `[{ currency, shareRial, unsettledRial }]`, one entry per
 * currency the day's lines are in (IRR first). It is EMPTY for a day with no
 * invoiced line — which has no currency; the card states that zero in the
 * clinic's (`currency` prop). `shareRial`/`unsettledRial` are that one currency's
 * figures, and NULL when the day spans two: nothing may show them as a total.
 */
export function toTodayIncome(r) {
  const visitCount = r.types?.find(item => item.itemType === 'VISIT')?.count || 0;
  const byCurrency = (Array.isArray(r.totalsByCurrency) ? r.totalsByCurrency : []).map(g => ({
    currency: g.currency || '',
    shareRial: g.doctorShareTotal || 0,
    unsettledRial: unsettledOf(g.doctorShareTotal, g.collectedDoctorShare),
  }));
  if (byCurrency.length > 1 || r.mixedCurrency) {
    return { shareRial: null, unsettledRial: null, visitCount, byCurrency };
  }
  if (byCurrency.length === 1) {
    return { shareRial: byCurrency[0].shareRial, unsettledRial: byCurrency[0].unsettledRial, visitCount, byCurrency };
  }
  return {
    shareRial: r.doctorShareTotal || 0,
    unsettledRial: unsettledOf(r.doctorShareTotal, r.collectedDoctorShare),
    visitCount,
    byCurrency,
  };
}

export function useTodayIncome(todayISO) {
  const [income, setIncome] = useState(null); // null = loading, false = unavailable

  useEffect(() => {
    if (!todayISO) return;
    let alive = true;
    const { start, end } = iranDayWindow(todayISO);
    bffQuery(TODAY_INCOME_QUERY, { fromTs: start, toTs: end - 1 })
      .then(data => {
        if (!alive) return;
        const r = data?.doctorAccountingReport;
        if (!r) { setIncome(false); return; }
        setIncome(toTodayIncome(r));
      })
      .catch(err => {
        console.error('todayIncome error:', err);
        if (alive) setIncome(false);
      });
    return () => { alive = false; };
  }, [todayISO]);

  return income;
}
